/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import {CreateGeoFenceTypeEnum} from '../../types/types';

type Crumb = {label?: string};

export type UseCreateGeofenceCardProps = {
  isCreateCard: boolean;
  show: boolean;
  selectedGeofenceType: CreateGeoFenceTypeEnum | null;
  setSelectedGeofenceType: (geofenceType: CreateGeoFenceTypeEnum) => void;
  changeSelectedGeofenceType: (geofenceType: CreateGeoFenceTypeEnum) => void;
  crumbs: Crumb[];
  selectCrumbs: (index: number, crumbs: Crumb[]) => void;
  clearActiveDrawing: () => void;
  setClickNext: (clickNext: boolean) => void;
  onClosePanel?: Function;
};

/**
 * Provides the current theme's create geofence card state for
 * CreateGeofences: whether its save step is shown (which sizes the card to
 * fit that step), the card's type switching and close handlers, and the
 * value for `CreateGeofenceCardContext` - `null` unless `isCreateCard` and
 * a type is selected. The card has no type list to pick from, so it opens
 * on radial whenever it's shown without a selected type.
 *
 * ```typescript
 *
 *   import {useCreateGeofenceCard} from
 *     '@ion/dls/src/maps/hooks/useCreateGeofenceCard';
 *   ...
 *
 *   const {isCreateCardSaveStep, createCardContext} = useCreateGeofenceCard({
 *     isCreateCard,
 *     show,
 *     selectedGeofenceType,
 *     setSelectedGeofenceType,
 *     changeSelectedGeofenceType,
 *     crumbs,
 *     selectCrumbs,
 *     clearActiveDrawing,
 *     setClickNext,
 *     onClosePanel : closeGeoFencePanel ?? onClose
 *   });
 *
 * ```
 *
 * @param   props The card flag, panel state and CreateGeofences' handlers.
 * @returns       The save step flag and the create card context value.
 */
export const useCreateGeofenceCard = ({
  isCreateCard,
  show,
  selectedGeofenceType,
  setSelectedGeofenceType,
  changeSelectedGeofenceType,
  crumbs,
  selectCrumbs,
  clearActiveDrawing,
  setClickNext,
  onClosePanel
}: UseCreateGeofenceCardProps) => {
  const [isCreateCardSaveStep, setIsCreateCardSaveStep] = useState(false);

  useEffect(() => {
    if (show && isCreateCard && !selectedGeofenceType) {
      changeSelectedGeofenceType(CreateGeoFenceTypeEnum.Radial);
    }
  }, [show, isCreateCard, selectedGeofenceType]);

  // switching types discards whatever was drawn for the previous type, then
  // keeps the same (hidden) type breadcrumb, relabelled ...
  const onChangeGeofenceType = useCallback((geofenceType: CreateGeoFenceTypeEnum) => {
    if (geofenceType !== selectedGeofenceType) {
      clearActiveDrawing();
      setClickNext(false);
      const typeCrumb = crumbs[crumbs.length - 1];
      if (typeCrumb) {
        typeCrumb.label = geofenceType;
      }
      setSelectedGeofenceType(geofenceType);
    }
  }, [selectedGeofenceType, clearActiveDrawing, crumbs]);

  const onCloseCreateCard = useCallback(() => {
    clearActiveDrawing();
    selectCrumbs(0, crumbs);
    onClosePanel?.();
  }, [clearActiveDrawing, selectCrumbs, crumbs, onClosePanel]);

  const createCardContext = useMemo(() => (
    isCreateCard && selectedGeofenceType ? {
      selectedGeofenceType,
      onChangeGeofenceType,
      onClose       : onCloseCreateCard,
      setIsSaveStep : setIsCreateCardSaveStep
    } : null
  ), [isCreateCard, selectedGeofenceType, onChangeGeofenceType, onCloseCreateCard]);

  return {isCreateCardSaveStep, createCardContext};
};

export default useCreateGeofenceCard;
